import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaTimes, FaBars, FaChevronDown } from "react-icons/fa";
import brandLogo from "../assets/brandLogo.png"
import { servicePages } from "./services/serviceData";

interface NavLink {
  label: string;
  href: string;
  target?: string;
  children?: NavLink[];
}

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    setServicesOpen(false);
  }, [location.pathname]);

  const navLinks: NavLink[] = [
    { label: "Home", href: "/" },
    { label: "How It Works", href: "/#how-it-works" },
    {
      label: "Services",
      href: "/services",
      children: servicePages.map((page) => ({ label: page.navLabel, href: `/${page.slug}` })),
    },
    { label: "FAQ", href: "/faq" },
    { label: "Contact Us", href: "/contact" },
    { label: "Repay Now", href: `${import.meta.env.VITE_WEBSITE_LINK}/repay-now`,target: "_blank" },
  ];

  const websiteLink = `${import.meta.env.VITE_WEBSITE_LINK}/?utm_source=Header&utm_medium=SEO`;
  const loginLink = `${import.meta.env.VITE_WEBSITE_LINK}/login`;

  return (
    <nav className="w-full fixed top-0 left-0 bg-white shadow-md z-[1000] px-4 lg:px-12 py-4 border-b border-gray-100">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img
            src={brandLogo}
            alt={`${import.meta.env.VITE_COMPANY_NAME} logo`}
            className="h-10 md:h-12 object-contain"
          />
        </a>

        {/* Desktop Menu */}
        <div className="hidden xl:flex items-center gap-6 2xl:gap-10 relative">
          {navLinks.map((link) => {
            if (link.children) {
              const isActive = link.children.some((child) => child.href === location.pathname);
              return (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    type="button"
                    onClick={() => setServicesOpen(!servicesOpen)}
                    aria-expanded={servicesOpen}
                    className={`flex items-center gap-1.5 whitespace-nowrap text-[12px] font-bold uppercase tracking-widest transition-all duration-300 relative pb-1 ${isActive || servicesOpen
                      ? "text-[var(--primary)]"
                      : "text-[var(--text-gray)] hover:text-[var(--primary)]"
                      }`}
                  >
                    {link.label}
                    <FaChevronDown size={9} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                    {isActive && (
                      <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--primary)] transition-all duration-300" />
                    )}
                  </button>
                  {servicesOpen && (
                    <div className="absolute left-1/2 top-full -translate-x-1/2 pt-4">
                      <div className="w-56 rounded-xl border border-gray-100 bg-white p-2 shadow-xl">
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`block rounded-lg px-4 py-2.5 text-[13px] font-semibold transition-colors ${location.pathname === child.href
                              ? "bg-[var(--primary-light)] text-[var(--primary)]"
                              : "text-[var(--text-gray)] hover:bg-gray-50 hover:text-[var(--primary)]"
                              }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            const isHome = link.href === "/";
            const isHashLink = link.href.startsWith("/#");
            const isExternal = link.target === "_blank" || link.href.startsWith("http");

            const isActive = isHashLink
              ? location.hash === link.href.substring(1)
              : isHome
                ? location.pathname === "/" && !location.hash
                : location.pathname === link.href;

            const className = `whitespace-nowrap text-[12px] font-bold uppercase tracking-widest transition-all duration-300 relative pb-1 ${isActive
              ? "text-[var(--primary)]"
              : "text-[var(--text-gray)] hover:text-[var(--primary)]"
              }`;

            const content = (
              <>
                {link.label}
                {isActive && (
                  <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--primary)] transition-all duration-300" />
                )}
              </>
            );

            return isExternal ? (
              <a
                key={link.label}
                href={link.href}
                target={link.target}
                rel="noopener noreferrer"
                className={className}
              >
                {content}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={className}
              >
                {content}
              </Link>
            );
          })}
          <a
            href={loginLink}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-4 px-6 py-2.5 border-2 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white font-bold text-[12px] uppercase tracking-widest rounded transition-all active:scale-95"
          >
            Login
          </a>
          <a
            href={websiteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 px-8 py-3 bg-[var(--primary)] text-white font-bold text-[12px] uppercase tracking-widest rounded transition-all hover:bg-[var(--primary-hover)] shadow-md hover:shadow-lg active:scale-95"
          >
            Apply Now
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="xl:hidden text-[var(--primary)] text-2xl"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {menuOpen && (
        <div className="absolute top-full left-0 w-full max-h-[calc(100vh-80px)] overflow-y-auto bg-white border-t border-gray-100 shadow-xl p-6 xl:hidden flex flex-col gap-6 animate-in slide-in-from-top duration-300">
          {navLinks.map((link) => {
            if (link.children) {
              const isActive = link.children.some((child) => child.href === location.pathname);
              return (
                <div key={link.label} className="flex flex-col gap-4">
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    aria-expanded={mobileServicesOpen}
                    className={`flex items-center justify-between font-bold uppercase text-sm tracking-widest transition-all ${isActive
                      ? "text-[var(--primary)] pl-2 border-l-4 border-[var(--primary)]"
                      : "text-[var(--text-gray)] hover:text-[var(--primary)]"
                      }`}
                  >
                    {link.label}
                    <FaChevronDown size={12} className={`transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="flex flex-col gap-4 pl-4 border-l border-gray-100">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`text-sm font-semibold transition-colors ${location.pathname === child.href
                            ? "text-[var(--primary)]"
                            : "text-[var(--text-gray)] hover:text-[var(--primary)]"
                            }`}
                          onClick={() => setMenuOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            const isHome = link.href === "/";
            const isHashLink = link.href.startsWith("/#");
            const isExternal = link.target === "_blank" || link.href.startsWith("http");

            const isActive = isHashLink
              ? location.hash === link.href.substring(1)
              : isHome
                ? location.pathname === "/" && !location.hash
                : location.pathname === link.href;

            const className = `font-bold uppercase text-sm tracking-widest transition-all ${isActive
              ? "text-[var(--primary)] pl-2 border-l-4 border-[var(--primary)]"
              : "text-[var(--text-gray)] hover:text-[var(--primary)] hover:pl-2"
              }`;

            return isExternal ? (
              <a
                key={link.label}
                href={link.href}
                target={link.target}
                rel="noopener noreferrer"
                className={className}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={className}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            );
          })}
          <a
            href={loginLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 border-2 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white font-bold uppercase text-center rounded tracking-widest text-sm transition-colors"
            onClick={() => setMenuOpen(false)}
          >
            Login
          </a>
          <a
            href={websiteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-4 bg-[var(--primary)] text-white font-bold uppercase text-center rounded tracking-widest text-sm shadow-md hover:bg-[var(--primary-hover)] transition-colors"
            onClick={() => setMenuOpen(false)}
          >
            Apply Now
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
